import type { SupportApi } from './support';
import type { Broadcast, Gift, ActivityItem, Design, DesignRecord, KhutbahLine, KhutbahSettings, Masjid, SavedTemplate, Schedule, Screen, ScreenBundle, TimetableDay } from '@noorview/core';

/**
 * Everything the dashboard and screens need from a server. Two implementations share this contract:
 * the sandbox (in this browser, for testing with virtual screens) and Supabase (production).
 */

export type Role = 'owner' | 'admin' | 'editor';
export interface Member { id: string; name: string; email: string; role: Role; joinedAt: string }
export interface Session {
  userId: string; name: string; email: string; role: Role;
  /** Noor MS in this masjid's NoorView with remote access (a Pro masjid gave it), until the time it set. */
  helping?: { masjidId: string; until: string };
  /**
   * A NoorClass account (made from noorclass.noormsystems.co.uk, which shares these sign-ins) with no masjid in
   * NoorView. It can sign in, but NoorView shows only "no access" until it has a masjid of its own.
   */
  noorclassOnly?: boolean;
}

export interface MasjidRegistration { masjidName: string; email: string; phone: string; holderName: string; password: string; agree: boolean; source?: 'website' | 'app' }

/** A phone that can use NoorView's widgets, Control Centre buttons and Siri without opening the app. */
export interface PhoneKey { id: string; userId: string; phoneName: string; createdAt: string; lastUsedAt: string | null }

export interface Presence { deviceId: string; screenId: string | null; lastSeenAt: string; version: number | null; userAgent?: string }

export interface Workspace {
  masjid: Masjid;
  screens: Screen[];
  designs: DesignRecord[];
  schedules: Schedule[];
  timetable: TimetableDay[];
  activity: ActivityItem[];
  members: Member[];
  presence: Presence[];
  /** Layouts saved by the masjid's editors. */
  templates: SavedTemplate[];
  /** Live messages and appeals (running, upcoming and recently ended). */
  broadcasts: Broadcast[];
  /** Seconds each screen's display was on and connected, per day, for the last 30 days. */
  uptime: Array<{ screenId: string; day: string; seconds: number }>;
}

export type NewBroadcast = Omit<Broadcast, 'id' | 'masjidId' | 'createdAt'>;

export type Change =
  | { kind: 'workspace' }
  | { kind: 'presence' }
  | { kind: 'design'; designId: string; revision: number; by: string };

export class ConflictError extends Error {
  constructor(public latest: DesignRecord) { super('Someone else saved this design.'); }
}

export interface Backend {
  readonly kind: 'sandbox' | 'supabase';
  /** Resolves once the saved sign-in (if any) has been restored. */
  ready(): Promise<void>;
  session(): Session | null;
  /** Masjid registration (noormsystems.co.uk/register and the app's Register screen). Real accounts only. */
  registerMasjid?(input: MasjidRegistration): Promise<void>;
  /** Enters the 5-digit masjid code from Noor MS: opens the masjid (the first time) and signs in. */
  redeemCode?(code: string): Promise<Session>;
  /** Sends a new code to the masjid's email. Returns what to tell the person. */
  resendCode?(email: string): Promise<string>;
  /** Changes the signed-in person's password. */
  changePassword?(password: string): Promise<void>;
  /**
   * Leaves the masjid (then signs out). The owner names who owns it next, if anyone else is on the team. The last
   * person leaving closes the masjid's NoorView account: everything is deleted and Noor MS is told.
   */
  leaveMasjid?(newOwnerId?: string | null): Promise<{ closed: boolean }>;
  /** First sign-in only: create the masjid this account will manage. */
  createMasjid?(input: { name: string; city: string; timezone: string; latitude: number; longitude: number; displayName: string }): Promise<void>;
  signIn(email: string, password: string): Promise<Session>;
  signOut(): Promise<void>;
  requestPasswordReset(email: string): Promise<void>;
  /** After following a reset link: choose the new password. */
  completePasswordReset?(password: string): Promise<void>;
  /**
   * What the emailed link this page was opened from asked for: choose a password (reset or invite), or nothing.
   * `error` is set when the link had expired or was already used.
   */
  authLink?(): { setPassword: boolean; error: string };
  /** Clears the remembered link once the new password is saved. */
  clearAuthLink?(): void;

  load(): Promise<Workspace>;
  subscribe(cb: (c: Change) => void): () => void;

  updateMasjid(patch: Partial<Omit<Masjid, 'id'>>): Promise<void>;
  createScreen(input: { name: string; design: Design }): Promise<Screen>;
  updateScreen(id: string, patch: Partial<Pick<Screen, 'name' | 'power' | 'locked' | 'designId' | 'salahBlank'>>): Promise<void>;
  updateScreens(ids: string[], patch: Partial<Pick<Screen, 'power' | 'locked'>>): Promise<void>;
  deleteScreen(id: string): Promise<void>;
  /** Links the device showing `code` to a screen. */
  pairScreen(code: string, screenId: string): Promise<void>;
  unpairScreen(screenId: string): Promise<void>;

  /** Saves the draft if nobody else saved since `baseRevision`; otherwise throws ConflictError. */
  saveDraft(designId: string, draft: Design, baseRevision: number, force?: boolean): Promise<{ revision: number; savedAt: string }>;
  publish(designId: string): Promise<{ version: number; publishedAt: string }>;
  createDesign(name: string, design: Design): Promise<DesignRecord>;
  renameDesign(id: string, name: string): Promise<void>;
  deleteDesign(id: string): Promise<void>;

  setTimetable(days: TimetableDay[], mode: 'merge' | 'replace' | 'days'): Promise<void>;
  upsertSchedule(s: Omit<Schedule, 'id' | 'masjidId' | 'createdAt'> & { id?: string }): Promise<Schedule>;
  deleteSchedule(id: string): Promise<void>;
  markActivityRead(ids?: string[]): Promise<void>;
  uploadMedia(file: Blob, name: string): Promise<string>;

  /** Live messages and appeals. */
  startBroadcast(b: NewBroadcast): Promise<Broadcast>;
  /** Changes a message or appeal that's on the screens or waiting to start (its gifts are never touched). */
  updateBroadcast(id: string, patch: Partial<Pick<Broadcast, 'startsAt' | 'endsAt' | 'screenIds' | 'placement' | 'message'>> & { target?: number; title?: string }): Promise<void>;
  endBroadcast(id: string): Promise<void>;
  addGift(id: string, gift: Omit<Gift, 'id' | 'at'>): Promise<void>;
  removeGift(id: string, giftId: string): Promise<void>;

  /** Live Khutbah (an add-on): its settings, start and stop, and what was said (live). */
  saveKhutbah(settings: KhutbahSettings): Promise<void>;
  startKhutbah(minutes?: number): Promise<void>;
  stopKhutbah(): Promise<void>;
  khutbahLines(broadcastId: string): Promise<KhutbahLine[]>;
  /** New and updated lines as they come (the first call has everything so far). */
  watchKhutbah(broadcastId: string, cb: (lines: KhutbahLine[]) => void): () => void;

  saveTemplate(name: string, design: Design): Promise<SavedTemplate>;
  renameTemplate(id: string, name: string): Promise<void>;
  deleteTemplate(id: string): Promise<void>;

  inviteMember(email: string, role: Role): Promise<void>;
  removeMember(id: string): Promise<void>;
  /** Admin or editor (the owner stays the owner). */
  updateMemberRole(id: string, role: Role): Promise<void>;

  /** Phone controls (iPhone widgets, Control Centre, Siri). Real accounts only, not the sandbox. */
  phoneKeys?(): Promise<PhoneKey[]>;
  /** Makes a key for this phone; it's only ever returned here. `replace` removes this phone's previous key. */
  createPhoneKey?(phoneName: string, replace?: string | null): Promise<{ id: string; key: string }>;
  removePhoneKey?(id: string): Promise<void>;

  /** The Support Centre: tickets, calls with Noor MS and remote access (see support.ts). */
  readonly support: SupportApi;

  /* ---------- device side (the screen app) ---------- */
  device: DeviceApi;
}

export interface DeviceApi {
  /** Registers this device (once) and returns its id plus the code to show while it waits to be paired. */
  register(existingId: string | null): Promise<{ deviceId: string; code: string | null; screenId: string | null }>;
  bundle(deviceId: string): Promise<ScreenBundle | null>;
  heartbeat(deviceId: string, version: number | null): Promise<void>;
  /** Fires when this device's screen changes (published, paired, power, schedules). */
  watch(deviceId: string, cb: () => void): () => void;
  /** A live khutbah on this screen: what was said so far, then new and updated lines as they come. */
  watchKhutbah(broadcastId: string, cb: (lines: KhutbahLine[]) => void): () => void;
  /**
   * The screen chosen to listen to the khutbah (real screens only): a short-lived speech-to-text key, each finished
   * sentence sent to be translated, and starting the khutbah by itself at Jumu'ah.
   */
  listen?: {
    token(): Promise<{ apiKey: string; model: string; endsAt: string }>;
    /** A sentence while it's spoken (`final` false: the words settled so far) and once it's finished. */
    translate(line: { seq: number; arabic: string; audioSeconds: number; final?: boolean }): Promise<void>;
    /** Sends the sentence being spoken to the screens as it's heard (not saved; the finished sentence replaces it). */
    partial(broadcastId: string): { send(seq: number, text: string): void; stop(): void };
    autoStart(slot: Date, ends: Date): Promise<void>;
  };
}
